import { Close, Emergency, NotificationsOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { ResponseTile } from "~ui/molecules/ResponseTile/ResponseTile";
import { EmergencyDispatchTileGrid } from "~ui/organisms/EmergencyDispatchTileGrid/EmergencyDispatchTileGrid";

const tTitle = "Emergency";
const tClose = "Close";

export interface QuickResponseTemplateProps {
  falseAlarmDisabled: boolean;
  onClose: () => void;
  onFalseAlarm: () => void;
  onTilePress: (actionId: string) => void;
  open: boolean;
  tileDisabled: boolean;
}

export function QuickResponseTemplate({
  falseAlarmDisabled,
  onClose,
  onFalseAlarm,
  onTilePress,
  open,
  tileDisabled,
}: QuickResponseTemplateProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      open={open}
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            m: fullScreen ? 0 : 2,
            maxHeight: fullScreen ? "100dvh" : "calc(100dvh - 48px)",
            overflow: "hidden",
          },
        },
      }}
      onClose={onClose}
    >
      <DialogTitle
        sx={{
          alignItems: "center",
          display: "flex",
          flexShrink: 0,
          gap: 1,
          justifyContent: "space-between",
          pr: 1,
          py: { md: 2, xs: 1.5 },
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex", gap: 1.5, minWidth: 0 }}>
          <Emergency color="error" sx={{ flexShrink: 0, fontSize: { md: 32, xs: 26 } }} />
          <Typography component="span" fontWeight={700} variant="h5">
            {tTitle}
          </Typography>
        </Box>
        <IconButton
          aria-label={tClose}
          color="inherit"
          edge="end"
          size="large"
          sx={{ flexShrink: 0 }}
          onClick={onClose}
        >
          <Close sx={{ fontSize: 28 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          gap: 1,
          minHeight: 0,
          overflow: fullScreen ? "hidden" : "auto",
          pb: { md: 2, xs: 1.5 },
          pt: 0,
        }}
      >
        <Typography color="text.secondary" sx={{ px: 0.5 }} variant="caption">
          Standard Response Protocol (SRP) uses five shared actions from the I Love U Guys model — see{" "}
          <Box
            component="a"
            href="https://iloveuguys.org/The-Standard-Response-Protocol.html"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
            target="_blank"
          >
            iloveuguys.org
          </Box>
          .
        </Typography>
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            gap: 1,
            minHeight: 0,
          }}
        >
          <EmergencyDispatchTileGrid disabled={tileDisabled} onPress={onTilePress} />
          <Box sx={{ display: "flex", flexShrink: 0, minHeight: 0 }}>
            <ResponseTile
              disabled={tileDisabled || falseAlarmDisabled}
              icon={<NotificationsOff />}
              kind="cancel"
              label="False Alarm"
              onPress={onFalseAlarm}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, justifyContent: "center", pb: 2, pt: 1, px: 2 }}>
        <Button fullWidth color="inherit" size="large" sx={{ maxWidth: 400 }} variant="outlined" onClick={onClose}>
          {tClose}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
